'use client';

import {
  IFormsTemplate,
  useFormsTemplates,
} from '@commenda/api/form-builder/forms-templates';
import {
  FeatureLaunch,
  isFeatureLaunched,
} from '@commenda/feature-launches';
import {
  HorizontalTabsList,
  Icons,
  Input,
  Tabs,
  TabsContent,
  TabsTrigger,
} from '@commenda/kakashi';
import { FormBuilderHook } from '../hooks/useFormBuilder';
import { FormsTemplateStatus, FormsTemplateType } from '@commenda/types';
import {
  FORM_BUILDER_DEBOUNCE_MS,
  FORM_BUILDER_DEFAULT_EXPANDED_FOR_COMPONENT_PALETTE,
  FORM_BUILDER_EDITOR_MODULES_PAGE_SIZE,
} from '@commenda/utils/form-builder';
import { useEffect, useMemo, useState } from 'react';
import { useDebounce } from '../../hooks/useDebounce';
import ComponentCategory from './ComponentPalette/ComponentCategory';
import SavedModulesSection from './ComponentPalette/SavedModulesSection';
import { COMPONENT_CATEGORIES } from './ComponentPalette/components';

type PaletteTab = 'components' | 'saved-modules';

export enum PaletteTabEnum {
  COMPONENTS = 'components',
  SAVED_MODULES = 'saved-modules',
}

export interface ComponentPaletteProps {
  formBuilder?: FormBuilderHook;
  onComponentAdded?: (id: string) => void;
}

export function ComponentPalette({
  formBuilder,
  onComponentAdded,
}: ComponentPaletteProps = {}) {
  const [allModules, setAllModules] = useState<IFormsTemplate[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<PaletteTab>(
    PaletteTabEnum.COMPONENTS
  );
  const [offset, setOffset] = useState(0);
  const debouncedSearch = useDebounce(searchQuery, FORM_BUILDER_DEBOUNCE_MS);

  useEffect(() => {
    setSearchQuery(debouncedSearch);
  }, [debouncedSearch]);

  // Fetch saved modules with debounce
  const {
    templates: savedModules,
    isLoading: isLoadingModules,
    offset: savedModulesOffset,
    count: savedModulesCount,
  } = useFormsTemplates({
    type: [FormsTemplateType.FORM_MODULE],
    status: [FormsTemplateStatus.LIVE, FormsTemplateStatus.COMPLETED],
    searchTerm: debouncedSearch,
    limit: FORM_BUILDER_EDITOR_MODULES_PAGE_SIZE,
    offset,
  });

  useEffect(() => {
    if (savedModulesOffset === 0) {
      // First page or search reset - replace all
      setAllModules(savedModules);
    } else if (savedModules && savedModules.length > 0) {
      // append new modules
      setAllModules((prev) => {
        const newModules = savedModules.filter(
          (m) => !prev.some((existing) => existing.id === m.id)
        );
        return [...prev, ...newModules];
      });
    }
  }, [savedModules, savedModulesOffset]);

  // Filter out Commenda OS categories if feature flag is not enabled
  const availableCategories = useMemo(() => {
    const isCommendaOSEnabled = isFeatureLaunched(
      FeatureLaunch.COMMENDA_OS_FORM_BUILDER
    );
    if (!isCommendaOSEnabled) {
      return COMPONENT_CATEGORIES.filter(
        (category) => !category.id.startsWith('commenda-os')
      );
    }
    return COMPONENT_CATEGORIES;
  }, []);

  // Filter categories based on search query for components
  const filteredCategories = searchQuery
    ? availableCategories
        .map((category) => ({
          ...category,
          components: category.components.filter(
            (c) =>
              c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
              c.description.toLowerCase().includes(searchQuery.toLowerCase())
          ),
        }))
        .filter((category) => category.components.length > 0)
    : availableCategories;

  return (
    <div className="h-full flex flex-col bg-bg-primary border-r border-border-secondary overflow-hidden">
      <div className="flex-1 flex flex-col min-h-0 py-2">
        <Tabs
          value={activeTab}
          onValueChange={(value) => setActiveTab(value as PaletteTab)}
          className="flex-1 flex flex-col min-h-0"
        >
          <HorizontalTabsList variant="underline">
            <TabsTrigger value="components">Components</TabsTrigger>
            <TabsTrigger value="saved-modules">Modules</TabsTrigger>
          </HorizontalTabsList>
          <TabsContent
            value="components"
            className="p-2 flex flex-col flex-1 min-h-0 gap-2"
          >
            <Input
              placeholder="Search components..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full"
              rightEl={() =>
                searchQuery ? (
                  <Icons.XClose
                    className="size-4 hover:text-text-quaternary-hover cursor-pointer text-text-quaternary"
                    onClick={() => setSearchQuery('')}
                  />
                ) : (
                  <Icons.SearchSm className="size-4 text-text-quaternary" />
                )
              }
            />
            <div className="flex-1 flex flex-col overflow-y-auto">
              {filteredCategories.length > 0 ? (
                filteredCategories.map((category) => (
                  <ComponentCategory
                    key={category.id}
                    category={category}
                    defaultExpanded={
                      FORM_BUILDER_DEFAULT_EXPANDED_FOR_COMPONENT_PALETTE
                    }
                    formBuilder={formBuilder}
                    onComponentAdded={onComponentAdded}
                  />
                ))
              ) : (
                <div className="flex-1 overflow-y-auto p-4">
                  <div className="text-center py-8">
                    <p className="txt-small-regular text-text-tertiary">
                      No components found{' '}
                      {searchQuery ? `matching "${searchQuery}"` : null}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>
          <TabsContent
            value="saved-modules"
            className="p-2 flex flex-col flex-1 min-h-0 gap-2"
          >
            <Input
              placeholder="Search modules..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              rightEl={() =>
                searchQuery ? (
                  <Icons.XClose
                    className="size-4 hover:text-text-quaternary-hover cursor-pointer text-text-quaternary"
                    onClick={() => setSearchQuery('')}
                  />
                ) : (
                  <Icons.SearchSm className="size-4 text-text-quaternary" />
                )
              }
              className="w-full"
            />
            <SavedModulesSection
              modules={allModules}
              isLoading={isLoadingModules}
              hasMore={savedModulesCount > allModules.length}
              onLoadMore={() => {
                setOffset(
                  savedModulesOffset + FORM_BUILDER_EDITOR_MODULES_PAGE_SIZE
                );
              }}
              defaultExpanded={
                FORM_BUILDER_DEFAULT_EXPANDED_FOR_COMPONENT_PALETTE
              }
            />
            <div className="flex-1 overflow-y-auto p-4">
              {allModules.length === 0 && !isLoadingModules && (
                <div className="text-center py-8">
                  <p className="txt-small-regular text-text-tertiary">
                    No modules found{' '}
                    {searchQuery ? `matching "${searchQuery}"` : null}
                  </p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

export default ComponentPalette;
